import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Plus, Edit, Trash2, X, Save, Upload, Calculator, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { ScrollArea } from "../../components/ui/scroll-area";
import { useLang } from "../../context/LangContext";
import { useCompany } from "../../context/CompanyContext";
import { formatMoney, currencySymbol } from "../../utils/currency";
import { calculateBasePrice, calculateFinalPrice, getLocalizedName, API } from "../../utils/format";
import { FieldHint } from "../../components/ui/FieldHint";
import { PercentInput } from "../../components/ui/PercentInput";
import NumberInput from "../../components/NumberInput";
import ProductTechSheetDialog from "../../components/ProductTechSheetDialog";
import { hasVal, parseNum, plPricing, plVisible } from "../../utils/pricelistPricing";
import QuickPricelistDialog from "./QuickPricelistDialog";
import { Zap } from "lucide-react";

// Colonne per listino nella tabella Prodotti: prezzo (fisso o da formula), provvigione e sconto per articolo, utile €/kg
const PL_COLORS = ["#8A1C2A", "#1F4E79", "#2E7D32", "#6A1B9A", "#B36B00", "#00695C", "#5D4037", "#37474F"];
const plColor = (idx) => PL_COLORS[idx % PL_COLORS.length];

const PlPriceCell = ({ prod, pt, sym, onChange, testId }) => {
  const { price, hasFixed, formula } = plPricing(prod, pt);
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState("");
  const ref = useRef(null);
  useEffect(() => { if (editing && ref.current) { ref.current.focus(); ref.current.select(); } }, [editing]);
  const overrides = prod.price_overrides || {};
  const commit = () => {
    setEditing(false);
    const v = parseNum(local);
    if (!v || v <= 0 || Math.abs(v - price) < 0.005) return;
    onChange({ ...overrides, [pt.id]: Math.round(v * 100) / 100 });
  };
  const unlock = () => { const next = { ...overrides }; delete next[pt.id]; onChange(next); };
  if (editing) {
    return <input ref={ref} value={local} onChange={(e) => setLocal(e.target.value)} onBlur={commit} inputMode="decimal"
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
      className="h-7 w-20 text-xs text-right bg-white border border-[#8A1C2A] rounded px-1" data-testid={`${testId}-input`} />;
  }
  return (
    <div className="flex items-center justify-end gap-1">
      <span onClick={() => { setLocal(price.toFixed(2)); setEditing(true); }} title={hasFixed ? `Prezzo fissato su questo listino (formula: ${formula.toFixed(2)} ${sym}). Clicca per cambiarlo.` : "Prezzo da formula. Clicca per scrivere un prezzo fisso per questo listino."}
        className={`cursor-pointer hover:bg-stone-100 px-1.5 py-0.5 rounded font-bold tabular-nums ${hasFixed ? "text-stone-900 bg-stone-100" : "text-[#8A1C2A]"}`} data-testid={testId}>{price.toFixed(2)}</span>
      {hasFixed && <button type="button" onClick={unlock} className="text-[11px] leading-none" title="Prezzo fissato: clicca per tornare alla formula" data-testid={`${testId}-unlock`}>🔒</button>}
    </div>
  );
};

const PlPctCell = ({ map, id, def, onChange, testId, title }) => {
  const has = hasVal(map?.[id]);
  const eff = has ? parseFloat(map[id]) : (def || 0);
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState("");
  const ref = useRef(null);
  useEffect(() => { if (editing && ref.current) { ref.current.focus(); ref.current.select(); } }, [editing]);
  const commit = () => {
    setEditing(false);
    const next = { ...(map || {}) };
    const raw = String(local).trim();
    if (raw === "") { delete next[id]; onChange(next); return; }
    const v = parseNum(raw);
    if (Number.isNaN(v) || v < 0 || v >= 100) return;
    const frac = Math.round(v * 100) / 10000;
    if (Math.abs(frac - (def || 0)) < 1e-9) delete next[id]; else next[id] = frac;
    onChange(next);
  };
  if (editing) {
    return <input ref={ref} value={local} onChange={(e) => setLocal(e.target.value)} onBlur={commit} inputMode="decimal" placeholder={`${((def || 0) * 100).toFixed(1)}`}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
      className="h-7 w-16 text-xs text-right bg-white border border-[#8A1C2A] rounded px-1" data-testid={`${testId}-input`} />;
  }
  return <span onClick={() => { setLocal(has ? (eff * 100).toFixed(1) : ""); setEditing(true); }} title={`${title}: ${has ? "valore per questo articolo (vuoto = standard listino)" : `standard del listino ${((def || 0) * 100).toFixed(1)}%`}`}
    className={`cursor-pointer hover:bg-stone-100 px-1.5 py-0.5 rounded tabular-nums inline-block min-w-[44px] text-right ${has ? "font-bold text-[#8A1C2A] bg-[#C5A059]/15" : "text-stone-500"}`} data-testid={testId}>{(eff * 100).toFixed(1)}%</span>;
};

const ProductsManagement = () => {
  const { t, lang } = useLang();
  const { company } = useCompany();
  const sym = currencySymbol(company);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pricelistTypes, setPricelistTypes] = useState([]);
  const [agentsList, setAgentsList] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [listinoFilter, setListinoFilter] = useState("all"); // all | true | false
  const [editProduct, setEditProduct] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [techSheetProduct, setTechSheetProduct] = useState(null);
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [hiddenPls, setHiddenPls] = useState(() => { try { return JSON.parse(localStorage.getItem("products-hidden-pricelists") || "[]"); } catch { return []; } });
  const [quickOpen, setQuickOpen] = useState(false);
  const togglePl = (id) => setHiddenPls((prev) => { const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]; localStorage.setItem("products-hidden-pricelists", JSON.stringify(next)); return next; });
  const visiblePls = pricelistTypes.filter((pt) => pt.is_active !== false && !hiddenPls.includes(pt.id));
  const [columnFilters, setColumnFilters] = useState({
    code: "", description: "", description_en: "", note: "", category: "",
    acq: "", bank: "", marg: "", extra: "",
  });
  const setFilter = (key, val) => setColumnFilters(p => ({ ...p, [key]: val }));
  const clearAllFilters = () => {
    setColumnFilters({ code: "", description: "", description_en: "", note: "", category: "", acq: "", bank: "", marg: "", extra: "" });
    setSearch("");
    setSelectedCategory("all");
    setListinoFilter("all");
  };
  const hasFilters = search || (selectedCategory !== "all") || (listinoFilter !== "all") || Object.values(columnFilters).some(v => v);
  const filterMatch = (val, q) => !q || String(val ?? "").toLowerCase().includes(q.toLowerCase());

  const filteredProducts = products.filter(p => {
    if (!filterMatch(p.code, columnFilters.code)) return false;
    if (!filterMatch(p.description, columnFilters.description)) return false;
    if (!filterMatch(p.description_en, columnFilters.description_en)) return false;
    if (!filterMatch(p.note, columnFilters.note)) return false;
    if (!filterMatch(p.category_name, columnFilters.category)) return false;
    if (columnFilters.acq && !filterMatch(p.acq?.toFixed(2), columnFilters.acq)) return false;
    if (columnFilters.bank && !filterMatch(p.bank?.toFixed(3), columnFilters.bank)) return false;
    if (columnFilters.marg && !filterMatch(p.marg?.toFixed(2), columnFilters.marg)) return false;
    if (columnFilters.extra && !filterMatch(p.extra?.toFixed(2), columnFilters.extra)) return false;
    return true;
  });

  const fetchProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (selectedCategory && selectedCategory !== "all") params.append("category_id", selectedCategory);
      if (listinoFilter && listinoFilter !== "all") params.append("in_listino", listinoFilter);
      params.append("active_only", "false");
      const response = await axios.get(`${API}/products?${params}`);
      setProducts(response.data);
    } catch { toast.error("Errore caricamento"); }
  }, [search, selectedCategory, listinoFilter]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (e) { /* silently ignore non-critical fetch errors */ console.debug(e); }
  };

  const fetchPricelistTypes = async () => {
    try {
      const response = await axios.get(`${API}/pricelist-types`);
      setPricelistTypes(response.data);
    } catch (e) { /* silently ignore non-critical fetch errors */ console.debug(e); }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`);
      setAgentsList(response.data || []);
    } catch (e) { /* silently ignore non-critical fetch errors */ console.debug(e); }
  };

  useEffect(() => { fetchCategories(); fetchPricelistTypes(); fetchAgents(); }, []);
  useEffect(() => {
    const d = setTimeout(() => fetchProducts(), 300);
    return () => clearTimeout(d);
  }, [fetchProducts]);

  const handleInlineUpdate = (productId, field, value) => {
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, [field]: value } : p));
    setPendingChanges(prev => ({ ...prev, [productId]: { ...prev[productId], [field]: value } }));
  };

  const saveAllChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) return;
    setSaving(true);
    try {
      await Promise.all(Object.entries(pendingChanges).map(([pid, updates]) =>
        axios.patch(`${API}/products/${pid}/inline`, updates)
      ));
      toast.success(`${Object.keys(pendingChanges).length} aggiornati`);
      setPendingChanges({});
      fetchPricelistTypes(); // aggiorna i conteggi "(N)" articoli attivi per listino
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const handleImport = async () => {
    if (!uploadFile) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", uploadFile);
    try {
      const response = await axios.post(`${API}/import/excel`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importati ${response.data.imported}`);
      setIsImportDialogOpen(false);
      setUploadFile(null);
      fetchProducts();
      fetchCategories();
    } catch { toast.error("Errore importazione"); }
    finally { setUploading(false); }
  };

  const saveProduct = async () => {
    try {
      if (editProduct?.id) {
        await axios.put(`${API}/products/${editProduct.id}`, editProduct);
        toast.success("Aggiornato");
      } else {
        await axios.post(`${API}/products`, editProduct);
        toast.success("Creato");
      }
      setIsDialogOpen(false);
      setEditProduct(null);
      fetchProducts();
    } catch { toast.error("Errore"); }
  };

  const EditableCell = ({ value, onChange, step = "0.01" }) => {
    const [editing, setEditing] = useState(false);
    const [localValue, setLocalValue] = useState(value);
    const inputRef = useRef(null);

    useEffect(() => { setLocalValue(value); }, [value]);
    useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);

    const handleBlur = () => { setEditing(false); if (localValue !== value) onChange(parseFloat(localValue) || 0); };

    if (editing) {
      return <NumberInput ref={inputRef} value={parseFloat(localValue) || 0} onChange={(e) => setLocalValue(e.target.value)} onBlur={handleBlur} onKeyDown={(e) => e.key === "Enter" && handleBlur()} className="h-7 w-20 text-xs bg-white border-[#8A1C2A]" />;
    }
    return <span onClick={() => setEditing(true)} className="cursor-pointer hover:bg-stone-100 px-2 py-1 rounded inline-block min-w-[40px] text-right">{typeof value === "number" ? value.toFixed(step === "0.001" ? 3 : 2) : value}</span>;
  };

  const ReverseFinalPriceCell = ({ pt, effective, discount = 0, computedPrice }) => {
    const priceOverrides = editProduct.price_overrides || {};
    const hasFixed = priceOverrides[pt.id] !== undefined && priceOverrides[pt.id] !== null;
    const [localPrice, setLocalPrice] = useState(
      hasFixed ? priceOverrides[pt.id].toFixed(2) : (computedPrice > 0 ? computedPrice.toFixed(2) : "")
    );
    useEffect(() => {
      const po = editProduct.price_overrides || {};
      const fixed = po[pt.id];
      if (fixed !== undefined && fixed !== null) {
        setLocalPrice(fixed.toFixed(2));
      } else {
        setLocalPrice(computedPrice > 0 ? computedPrice.toFixed(2) : "");
      }
    // eslint-disable-next-line
    }, [computedPrice, editProduct.price_overrides]);

    const commit = () => {
      const np = parseFloat(localPrice);
      if (!np || np <= 0) { setLocalPrice(hasFixed ? priceOverrides[pt.id].toFixed(2) : computedPrice.toFixed(2)); return; }
      if (hasFixed) {
        // Already in "fixed" mode → just update the fixed price
        const next = { ...priceOverrides, [pt.id]: np };
        setEditProduct({ ...editProduct, price_overrides: next });
        return;
      }
      if (Math.abs(np - computedPrice) < 0.005) return;
      // Back-calc margin (global mode) — INVERSE of new margin-style formula:
      //   final = base / (1-markup) / (1-commission) * (1-discount)
      //   base  = (acq / (1-marg)) + acq*bank + extra
      // Step 1: undo discount/commission/markup to get base
      // Step 2: solve marg from base
      const markup = pt.markup || 0;
      const disc = discount || 0;
      const eff = effective || 0;
      const priceBeforeDiscount = (disc > 0 && disc < 1) ? (np / (1 - disc)) : np;
      const newBase = priceBeforeDiscount * (1 - eff) * (1 - markup);
      const acq = editProduct.acq || 0;
      const bank = editProduct.bank || 0;
      const extra = editProduct.extra || 0;
      if (acq <= 0) { toast.error("ACQ deve essere > 0"); setLocalPrice(computedPrice.toFixed(2)); return; }
      const denom = newBase - acq * bank - extra;
      if (denom <= 0) { toast.error(`Prezzo troppo basso (deve coprire ACQ·Bank + EXTRA)`); setLocalPrice(computedPrice.toFixed(2)); return; }
      const newMarg = 1 - acq / denom;
      if (newMarg < 0 || newMarg >= 1) { toast.error(`Margine fuori range: ${(newMarg*100).toFixed(1)}%`); setLocalPrice(computedPrice.toFixed(2)); return; }
      const roundedMarg = Math.round(newMarg * 10000) / 10000;
      setEditProduct({ ...editProduct, marg: roundedMarg });
      toast.success(`Margine globale ricalcolato: ${(roundedMarg*100).toFixed(2)}%`);
    };

    const toggleFixed = () => {
      const next = { ...priceOverrides };
      if (hasFixed) {
        // Remove override → revert to formula
        delete next[pt.id];
        toast.success(`Prezzo "${pt.name}" tornato a calcolo formula`);
      } else {
        // Lock current displayed price as fixed override for THIS pricelist only
        const lockedPrice = parseFloat(localPrice) || computedPrice;
        next[pt.id] = lockedPrice;
        toast.success(`Prezzo "${pt.name}" fissato a ${lockedPrice.toFixed(2)}${sym} (solo questo listino)`);
      }
      setEditProduct({ ...editProduct, price_overrides: next });
    };

    return (
      <div className="flex items-center gap-1">
        <NumberInput
          value={parseFloat(localPrice) || 0}
          onChange={(e) => setLocalPrice(String(e.target.value))}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
          className={`h-8 text-sm font-bold text-right border-stone-300 focus:border-[#8A1C2A] ${hasFixed ? 'bg-stone-100 text-stone-800' : 'bg-white text-[#8A1C2A]'}`}
          data-testid={`final-price-${pt.id}`}
          title={hasFixed ? "Prezzo fissato manualmente per questo listino" : "Inserisci il prezzo finale per ricalcolare il margine globale (impatta TUTTI i listini)"}
        />
        <button
          type="button"
          onClick={toggleFixed}
          className={`p-1.5 rounded border transition-all ${hasFixed ? 'bg-stone-700 border-stone-700 text-white' : 'bg-white border-stone-300 text-stone-500 hover:bg-stone-50'}`}
          data-testid={`lock-price-${pt.id}`}
          title={hasFixed ? "Sblocca: torna a calcolo formula globale" : "Blocca: fissa il prezzo solo per questo listino (modifica al volo, non tocca il margine globale)"}
        >
          {hasFixed ? "🔒" : "🔄"}
        </button>
      </div>
    );
  };

  const EditableTextCell = ({ value, onChange, placeholder = "", testId }) => {
    const [editing, setEditing] = useState(false);
    const [localValue, setLocalValue] = useState(value || "");
    const inputRef = useRef(null);

    useEffect(() => { setLocalValue(value || ""); }, [value]);
    useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);

    const commit = () => { setEditing(false); if (localValue !== (value || "")) onChange(localValue); };

    if (editing) {
      return <Input ref={inputRef} value={localValue} onChange={(e) => setLocalValue(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setLocalValue(value || ""); setEditing(false); } }}
        placeholder={placeholder} className="h-7 text-xs bg-white border-[#8A1C2A]" data-testid={testId} />;
    }
    return (
      <span onClick={() => setEditing(true)} className="cursor-pointer hover:bg-stone-100 px-2 py-1 rounded inline-block min-w-[120px] text-stone-600 text-sm" data-testid={testId}>
        {value || <span className="text-stone-300 italic">— click to add EN —</span>}
      </span>
    );
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-2xl font-display font-semibold text-stone-900">{t.products}</h2>
          <p className="text-stone-500 text-sm mt-1">
            {hasFilters ? `${filteredProducts.length} / ${products.length}` : products.length} prodotti — Clicca sui valori per modificare
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {hasFilters && (
            <Button onClick={clearAllFilters} variant="outline" size="sm" className="border-stone-300 text-stone-600 hover:bg-stone-50" data-testid="clear-filters-products-btn">
              <X className="w-4 h-4 mr-1" />Cancella filtri
            </Button>
          )}
          {hasPendingChanges && (
            <Button onClick={saveAllChanges} disabled={saving} className="bg-[#C5A059] hover:bg-[#B08D4A] text-white" data-testid="save-changes-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? "..." : `${t.save} (${Object.keys(pendingChanges).length})`}
            </Button>
          )}
          <Button onClick={() => setQuickOpen(true)} variant="outline" className="border-[#C5A059] text-[#8A1C2A] hover:bg-[#C5A059]/10" data-testid="products-quick-pricelist-btn" title="Crea un listino con solo alcuni articoli, impostando provvigioni e margini">
            <Zap className="w-4 h-4 mr-2" />Listino rapido
          </Button>
          <Button onClick={() => setIsImportDialogOpen(true)} variant="outline" className="border-[#C5A059] text-[#8A1C2A] hover:bg-[#C5A059]/10">
            <Upload className="w-4 h-4 mr-2" />{t.import}
          </Button>
          <Button onClick={() => { setEditProduct({ code: "", description: "", description_en: "", category_id: categories[0]?.id || "", acq: 0, bank: 0.015, marg: 0.20, extra: 0.50, note: "", availability: "DISPONIBILE", is_active: true, in_listino: true, restricted_pricelist_types: [], restricted_agents: [], vat_rate: 0.10 }); setIsDialogOpen(true); }} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="new-product-btn">
            <Plus className="w-4 h-4 mr-2" />{t.new}
          </Button>
          <Button onClick={() => { setEditProduct({ code: "", description: "", description_en: "", category_id: categories[0]?.id || "", acq: 0, bank: 0.015, marg: 0.20, extra: 0, note: "", availability: "DISPONIBILE", is_active: true, in_listino: false, restricted_pricelist_types: [], restricted_agents: [], vat_rate: 0.10 }); setIsDialogOpen(true); }} variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50" data-testid="new-extra-product-btn" title="Articolo non visibile nel listino cliente/agente — disponibile solo per il magazzino in fase di evasione">
            <Plus className="w-4 h-4 mr-2" />Fuori Listino
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input placeholder={t.search} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white border-stone-300" data-testid="products-search" />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full sm:w-56 bg-white border-stone-300"><SelectValue placeholder={t.allCategories} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.allCategories}</SelectItem>
            {categories.map(cat => <SelectItem key={cat.id} value={cat.id}>{getLocalizedName(cat, lang)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={listinoFilter} onValueChange={setListinoFilter}>
          <SelectTrigger className="w-full sm:w-44 bg-white border-stone-300" data-testid="listino-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.allProducts}</SelectItem>
            <SelectItem value="true">{t.onlyInListino}</SelectItem>
            <SelectItem value="false">{t.onlyOutOfListino}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {pricelistTypes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="products-pricelist-chips">
          <span className="text-xs font-semibold uppercase text-stone-500">Listini in tabella:</span>
          {pricelistTypes.map((pt, i) => {
            const on = !hiddenPls.includes(pt.id);
            return (
              <button key={pt.id} type="button" onClick={() => togglePl(pt.id)} data-testid={`pl-chip-${pt.id}`}
                className={`px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${on ? "text-white" : "bg-white text-stone-500 border-stone-300"}`}
                style={on ? { background: plColor(i), borderColor: plColor(i) } : undefined}
                title={`${pt.name} · provv. ${((pt.commission || 0) * 100).toFixed(1)}% · ricarico ${((pt.markup || 0) * 100).toFixed(1)}% · sconto ${((pt.discount || 0) * 100).toFixed(1)}% · ${pt.active_count ?? "?"} articoli attivi${pt.quick ? " · listino rapido" : ""}`}>
                {pt.name}{pt.active_count != null ? ` (${pt.active_count})` : ""}
              </button>
            );
          })}
          <span className="text-[11px] text-stone-400">Per ogni listino: prezzo (clicca per fissarlo), provvigione %, sconto %, utile {sym}/kg, attivo sì/no</span>
        </div>
      )}

      <Card className="bg-white border-stone-200 shadow-sm">
        <div className="max-h-[600px] overflow-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full" style={{ minWidth: `${1100 + visiblePls.length * 360}px` }}>
            <thead className="sticky top-0 z-10">
              <tr>
                <th colSpan={12} className="bg-[#5a0f19] text-white text-[10px] uppercase tracking-wider p-1 text-left">Articolo e costi</th>
                {visiblePls.map((pt) => (
                  <th key={pt.id} colSpan={5} className="text-white text-[10px] uppercase tracking-wider p-1 border-l-2 border-white/60" style={{ background: plColor(pricelistTypes.indexOf(pt)) }} data-testid={`pl-group-${pt.id}`}
                    title={`Provvigione standard ${((pt.commission || 0) * 100).toFixed(1)}% · ricarico ${((pt.markup || 0) * 100).toFixed(1)}% · sconto ${((pt.discount || 0) * 100).toFixed(1)}%`}>
                    {pt.name} <span className="normal-case opacity-80">· provv. {((pt.commission || 0) * 100).toFixed(1)}% · ricarico {((pt.markup || 0) * 100).toFixed(0)}%</span>
                  </th>
                ))}
                <th className="bg-[#5a0f19] p-1"></th>
              </tr>
              <tr className="bg-[#8A1C2A]">
                <th className="text-center p-2 text-xs font-semibold uppercase text-white w-14" title="Immagine caricata?">📷</th>
                <th className="text-left p-3 text-xs font-semibold uppercase text-white">{t.code}</th>
                <th className="text-left p-3 text-xs font-semibold uppercase text-white">🇮🇹 {t.description}</th>
                <th className="text-left p-3 text-xs font-semibold uppercase text-white bg-[#6B1420]">🇬🇧 {t.descEn}</th>
                <th className="text-left p-3 text-xs font-semibold uppercase text-white bg-[#6B1420]">📝 {t.note}</th>
                <th className="text-left p-3 text-xs font-semibold uppercase text-white">{t.category}</th>
                <th className="text-center p-3 text-xs font-semibold uppercase text-white">{t.unit}</th>
                <th className="text-right p-3 text-xs font-semibold uppercase text-white">ACQ {sym}</th>
                <th className="text-right p-3 text-xs font-semibold uppercase text-white">Bank</th>
                <th className="text-right p-3 text-xs font-semibold uppercase text-white">Marg</th>
                <th className="text-right p-3 text-xs font-semibold uppercase text-white">Extra</th>
                <th className="text-center p-3 text-xs font-semibold uppercase text-white">{t.active}</th>
                {visiblePls.map((pt) => (
                  <React.Fragment key={pt.id}>
                    <th className="text-right p-2 text-xs font-semibold uppercase text-white border-l-2 border-white/60">Prezzo {sym}</th>
                    <th className="text-right p-2 text-xs font-semibold uppercase text-white">Provv.</th>
                    <th className="text-right p-2 text-xs font-semibold uppercase text-white">Sconto</th>
                    <th className="text-right p-2 text-xs font-semibold uppercase text-white" title="Prezzo − costo (ACQ + bank + extra) − provvigione">Utile {sym}</th>
                    <th className="text-center p-2 text-xs font-semibold uppercase text-white" title="Articolo attivo (visibile e vendibile) in questo listino">Attivo</th>
                  </React.Fragment>
                ))}
                <th className="text-right p-3 text-xs font-semibold uppercase text-white">{t.actions}</th>
              </tr>
              <tr className="bg-stone-50 border-b-2 border-[#8A1C2A]">
                <th className="p-1"></th>
                <th className="p-1"><Input value={columnFilters.code} onChange={(e) => setFilter('code', e.target.value)} placeholder={"🔍 " + t.code} className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400" data-testid="filter-prod-code" /></th>
                <th className="p-1"><Input value={columnFilters.description} onChange={(e) => setFilter('description', e.target.value)} placeholder={"🔍 " + t.description} className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400" data-testid="filter-prod-description" /></th>
                <th className="p-1"><Input value={columnFilters.description_en} onChange={(e) => setFilter('description_en', e.target.value)} placeholder="🔍 EN" className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400" data-testid="filter-prod-desc-en" /></th>
                <th className="p-1"><Input value={columnFilters.note} onChange={(e) => setFilter('note', e.target.value)} placeholder={"🔍 " + t.note} className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400" data-testid="filter-prod-note" /></th>
                <th className="p-1"><Input value={columnFilters.category} onChange={(e) => setFilter('category', e.target.value)} placeholder={"🔍 " + t.category} className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400" data-testid="filter-prod-category" /></th>
                <th className="p-1"></th>
                <th className="p-1"><Input value={columnFilters.acq} onChange={(e) => setFilter('acq', e.target.value)} placeholder="🔍" className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400 text-right" data-testid="filter-prod-acq" /></th>
                <th className="p-1"><Input value={columnFilters.bank} onChange={(e) => setFilter('bank', e.target.value)} placeholder="🔍" className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400 text-right" data-testid="filter-prod-bank" /></th>
                <th className="p-1"><Input value={columnFilters.marg} onChange={(e) => setFilter('marg', e.target.value)} placeholder="🔍" className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400 text-right" data-testid="filter-prod-marg" /></th>
                <th className="p-1"><Input value={columnFilters.extra} onChange={(e) => setFilter('extra', e.target.value)} placeholder="🔍" className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400 text-right" data-testid="filter-prod-extra" /></th>
                <th className="p-1"></th>
                {visiblePls.map((pt) => <th key={pt.id} colSpan={5} className="p-1 border-l-2 border-stone-200"></th>)}
                <th className="p-1"></th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((prod, idx) => (
                <tr key={prod.id} className={`border-b border-stone-200 hover:bg-stone-50 ${pendingChanges[prod.id] ? 'bg-[#C5A059]/10' : ''}`} data-testid={`product-row-${prod.code}`}>
                  <td className="p-1 w-14 text-center">
                    <button
                      type="button"
                      onClick={() => setTechSheetProduct(prod)}
                      className={`inline-flex items-center justify-center w-12 h-12 rounded border transition ${prod.image_path ? "border-stone-200 hover:border-[#8A1C2A] overflow-hidden bg-white" : "border-dashed border-stone-300 hover:border-[#8A1C2A] hover:bg-stone-50 bg-stone-50"}`}
                      title={prod.image_path ? "Immagine caricata — clicca per cambiare" : "Nessuna immagine — clicca per caricare"}
                      data-testid={`product-thumb-${prod.code}`}
                    >
                      {prod.image_path ? (
                        <img
                          src={prod.image_path.startsWith("http") ? prod.image_path : `${process.env.REACT_APP_BACKEND_URL}/api/files/${prod.image_path}`}
                          alt={prod.description}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <Upload className="w-4 h-4 text-stone-400" />
                      )}
                    </button>
                  </td>
                  <td className="p-3 text-xs font-mono text-[#8A1C2A] font-semibold">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span>{prod.code}</span>
                      {prod.in_listino === false && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[9px] px-1 py-0 leading-tight" title="Articolo non visibile nel listino cliente — disponibile solo per il magazzino in fase di evasione">Fuori Listino</Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-sm text-stone-900 truncate max-w-[220px]" title={prod.description}>{prod.description}</td>
                  <td className="p-3 max-w-[260px] bg-[#FFF8EC]/30">
                    <EditableTextCell
                      value={prod.description_en}
                      onChange={(v) => handleInlineUpdate(prod.id, "description_en", v)}
                      placeholder="English description..."
                      testId={`desc-en-${prod.code}`}
                    />
                  </td>
                  <td className="p-3 max-w-[220px] bg-[#FFF8EC]/30">
                    <EditableTextCell
                      value={prod.note}
                      onChange={(v) => handleInlineUpdate(prod.id, "note", v)}
                      placeholder="es. Minimo 500kg, su ordinazione..."
                      testId={`note-${prod.code}`}
                    />
                  </td>
                  <td className="p-3"><Badge variant="outline" className="border-stone-300 text-stone-600 text-xs">{prod.category_name}</Badge></td>
                  <td className="p-3 text-center">
                    <Select value={prod.unit || "Kg"} onValueChange={(v) => handleInlineUpdate(prod.id, "unit", v)}>
                      <SelectTrigger className="h-7 w-20 text-xs bg-white border-stone-300 mx-auto" data-testid={`unit-${prod.code}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Kg">Kg</SelectItem>
                        <SelectItem value="Pz">Pz</SelectItem>
                        <SelectItem value="CT">CT</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-right text-stone-800"><EditableCell value={prod.acq} onChange={(v) => handleInlineUpdate(prod.id, "acq", v)} /></td>
                  <td className="p-3 text-right text-stone-600"><EditableCell value={prod.bank} step="0.001" onChange={(v) => handleInlineUpdate(prod.id, "bank", v)} /></td>
                  <td className="p-3 text-right text-stone-600"><EditableCell value={prod.marg} onChange={(v) => handleInlineUpdate(prod.id, "marg", v)} /></td>
                  <td className="p-3 text-right text-stone-600"><EditableCell value={prod.extra} onChange={(v) => handleInlineUpdate(prod.id, "extra", v)} /></td>
                  <td className="p-3 text-center">
                    <Switch checked={prod.is_active} onCheckedChange={(v) => handleInlineUpdate(prod.id, "is_active", v)} className="data-[state=checked]:bg-green-500" />
                  </td>
                  {visiblePls.map((pt) => {
                    const pr = plPricing(prod, pt);
                    const on = plVisible(prod, pt);
                    const dim = on ? "" : "opacity-35";
                    return (
                      <React.Fragment key={pt.id}>
                        <td className={`p-2 text-right text-xs border-l-2 border-stone-200 ${dim}`}><PlPriceCell prod={prod} pt={pt} sym={sym} onChange={(m) => handleInlineUpdate(prod.id, "price_overrides", m)} testId={`pl-price-${pt.id}-${prod.code}`} /></td>
                        <td className={`p-2 text-right text-xs ${dim}`}><PlPctCell map={prod.commission_overrides} id={pt.id} def={pt.commission} title="Provvigione" onChange={(m) => handleInlineUpdate(prod.id, "commission_overrides", m)} testId={`pl-comm-${pt.id}-${prod.code}`} /></td>
                        <td className={`p-2 text-right text-xs ${dim}`}><PlPctCell map={prod.discount_overrides} id={pt.id} def={pt.discount} title="Sconto" onChange={(m) => handleInlineUpdate(prod.id, "discount_overrides", m)} testId={`pl-disc-${pt.id}-${prod.code}`} /></td>
                        <td className={`p-2 text-right text-xs tabular-nums font-semibold ${dim} ${pr.profit < 0 ? "text-red-600" : pr.profit < 0.5 ? "text-amber-600" : "text-green-700"}`} title={`Prezzo ${pr.price.toFixed(2)} − costo ${pr.cost.toFixed(2)} − provvigione ${(pr.price * pr.comm).toFixed(2)}`} data-testid={`pl-profit-${pt.id}-${prod.code}`}>{pr.profit.toFixed(2)}</td>
                        <td className="p-2 text-center"><Switch checked={on} onCheckedChange={(v) => handleInlineUpdate(prod.id, "pricelist_active", { ...(prod.pricelist_active || {}), [pt.id]: v })} className="data-[state=checked]:bg-green-500 scale-90" title={on ? "Attivo in questo listino: clicca per disattivarlo" : "Non attivo in questo listino: clicca per attivarlo"} data-testid={`pl-active-${pt.id}-${prod.code}`} /></td>
                      </React.Fragment>
                    );
                  })}
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setTechSheetProduct(prod)} className="h-7 w-7 p-0 hover:bg-amber-50" title="Scheda tecnica + immagine" data-testid={`tech-sheet-open-${prod.id}`}><FileText className="w-3 h-3 text-[#C5A059]" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEditProduct(prod); setIsDialogOpen(true); }} className="h-7 w-7 p-0 hover:bg-stone-100"><Edit className="w-3 h-3 text-stone-500" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => axios.delete(`${API}/products/${prod.id}`).then(fetchProducts)} className="h-7 w-7 p-0 hover:bg-red-50"><Trash2 className="w-3 h-3 text-red-500" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit Product Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-white border-stone-200 max-w-2xl">
          <DialogHeader><DialogTitle className="text-stone-900">{editProduct?.id ? t.edit : t.new} Prodotto</DialogTitle></DialogHeader>
          {editProduct && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-stone-700">{t.code}</Label>
                  <Input value={editProduct.code || ""} onChange={(e) => setEditProduct({ ...editProduct, code: e.target.value })} className="bg-white border-stone-300" />
                </div>
                <div className="space-y-2">
                  <Label className="text-stone-700">{t.category}</Label>
                  <Select value={editProduct.category_id} onValueChange={(v) => setEditProduct({ ...editProduct, category_id: v })}>
                    <SelectTrigger className="bg-white border-stone-300"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-stone-700 flex items-center gap-2">🇮🇹 {t.description} (IT)</Label>
                <Input value={editProduct.description || ""} onChange={(e) => setEditProduct({ ...editProduct, description: e.target.value })} className="bg-white border-stone-300" />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-700 flex items-center gap-2">🇬🇧 {t.descEn}</Label>
                <Input value={editProduct.description_en || ""} onChange={(e) => setEditProduct({ ...editProduct, description_en: e.target.value })} placeholder="English description (opzionale)" className="bg-white border-stone-300" data-testid="product-desc-en-input" />
              </div>
              <div className={`flex items-center justify-between p-3 rounded-lg border ${editProduct.in_listino === false ? 'bg-amber-50 border-amber-300' : 'bg-stone-50 border-stone-200'}`}>
                <div>
                  <Label className="text-sm font-semibold text-stone-800 flex items-center gap-1">
                    {editProduct.in_listino === false ? "📌 Articolo Fuori Listino" : "📋 Articolo In Listino"}
                    <FieldHint>
                      <strong>In Listino</strong>: il prodotto appare nei cataloghi, listini PDF, portali cliente/agente. È vendibile normalmente.<br/><br/>
                      <strong>Fuori Listino</strong>: il prodotto è "nascosto", invisibile a clienti e agenti. Può essere aggiunto manualmente dal magazzino in fase di evasione ordine (utile per articoli stagionali, promozionali, fuori catalogo).
                    </FieldHint>
                  </Label>
                  <p className="text-xs text-stone-600 mt-0.5">
                    {editProduct.in_listino === false
                      ? "Non visibile a clienti/agenti. Aggiungibile dal magazzino in fase di evasione."
                      : "Visibile nel listino cliente, catalogo agente e PDF listino."}
                  </p>
                </div>
                <Switch
                  checked={editProduct.in_listino !== false}
                  onCheckedChange={(v) => setEditProduct({ ...editProduct, in_listino: v })}
                  className="data-[state=checked]:bg-[#8A1C2A]"
                  data-testid="product-in-listino-switch"
                />
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label className="text-stone-700 text-xs flex items-center gap-1">📦 U.M.
                    <FieldHint>
                      <strong>Unità di misura primaria</strong> con cui viene venduto il prodotto:<br/>
                      • <strong>Kg</strong> — vendita al peso<br/>
                      • <strong>Pz</strong> — vendita al pezzo<br/>
                      • <strong>CT</strong> — vendita a cartone<br/><br/>
                      Il cliente potrà comunque scegliere una UoM diversa al momento dell'ordine.
                    </FieldHint>
                  </Label>
                  <Select value={editProduct.unit || "Kg"} onValueChange={(v) => setEditProduct({ ...editProduct, unit: v })}>
                    <SelectTrigger className="bg-white border-stone-300" data-testid="product-unit-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Kg">Kg</SelectItem>
                      <SelectItem value="Pz">Pz</SelectItem>
                      <SelectItem value="CT">CT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-stone-700 text-xs flex items-center gap-1">ACQ {sym}
                    <FieldHint>
                      <strong>Costo di acquisto</strong> del prodotto (prezzo pagato al fornitore). Base per il calcolo del prezzo di vendita finale. Inserisci in €/Kg (es. <code>10,50</code>). Accetta sia la virgola che il punto come separatore decimale.
                    </FieldHint>
                  </Label>
                  <NumberInput value={editProduct.acq || 0} onChange={(e) => setEditProduct({ ...editProduct, acq: parseFloat(e.target.value) || 0 })} className="bg-white border-stone-300" data-testid="product-acq-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-stone-700 text-xs flex items-center gap-1">Bank %
                    <FieldHint>
                      <strong>Costo bancario/finanziario</strong> in percentuale (digita <code>1,5</code> per 1,5%). Si applica come <em>additivo</em> sul costo di acquisto: <code>+ (ACQ × Bank%)</code>. Esempio: ACQ 10€ con Bank 1,5% aggiunge 0,15€.
                    </FieldHint>
                  </Label>
                  <PercentInput value={editProduct.bank || 0} onChange={(v) => setEditProduct({ ...editProduct, bank: v })} data-testid="product-bank-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-stone-700 text-xs flex items-center gap-1">Marg %
                    <FieldHint>
                      <strong>Margine sul prezzo di vendita</strong> in percentuale (digita <code>20</code> per 20%). Si applica come <em>margine</em> (non additivo): <code>ACQ / (1 - Marg%)</code>. Esempio: ACQ 10€ con Marg 20% → 10 / 0,80 = 12,50€ (cioè il costo è l'80% del prezzo base).
                    </FieldHint>
                  </Label>
                  <PercentInput value={editProduct.marg || 0} onChange={(v) => setEditProduct({ ...editProduct, marg: v })} data-testid="product-marg-input" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="text-stone-700 text-xs flex items-center gap-1">Extra {sym}
                    <FieldHint>
                      <strong>Costo extra fisso</strong> ({sym}) sommato al prezzo per coprire spese aggiuntive (lavorazione, packaging, trasporto interno...).
                    </FieldHint>
                  </Label>
                  <NumberInput value={editProduct.extra || 0} onChange={(e) => setEditProduct({ ...editProduct, extra: parseFloat(e.target.value) || 0 })} className="bg-white border-stone-300" data-testid="product-extra-input" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="text-stone-700 text-xs flex items-center gap-1">IVA %
                    <FieldHint>
                      <strong>Aliquota IVA</strong> applicata al prodotto. Digita <code>10</code> per 10%, <code>22</code> per 22%, <code>0</code> per esente. Su clienti esteri (vedi anagrafica cliente) l'IVA è sempre azzerata.
                    </FieldHint>
                  </Label>
                  <PercentInput value={editProduct.vat_rate ?? 0.10} onChange={(v) => setEditProduct({ ...editProduct, vat_rate: v })} placeholder="Es. 10 per 10%" data-testid="product-vat-input" />
                </div>
              </div>
              <div className="bg-stone-50 p-3 rounded border border-stone-200 flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-stone-600">{t.basePrice}: <span className="font-bold text-[#8A1C2A]">{formatMoney(calculateBasePrice(editProduct.acq || 0, editProduct.bank || 0, editProduct.marg || 0, editProduct.extra || 0), company)}</span></p>
                <p className="text-sm text-stone-600">Margine: <span className="font-bold text-[#8A1C2A]">{((editProduct.marg || 0) * 100).toFixed(2)}%</span></p>
              </div>

              {/* Visibilità prodotto: attivo per listino + agenti autorizzati */}
              <div className="space-y-3 pt-2 border-t border-stone-200">
                <div>
                  <Label className="text-stone-700 flex items-center gap-2 font-semibold">
                    🎯 Visibilità del prodotto
                    <FieldHint>
                      <strong>Attivo nei listini</strong> → l'articolo è visibile e vendibile solo nei listini con la spunta (stessa cosa della colonna "Attivo" in tabella).<br/><br/>
                      <strong>Agenti autorizzati</strong> → il prodotto appare SOLO ai clienti gestiti da quegli agenti (vuoto = tutti).<br/><br/>
                      Le due restrizioni si combinano in AND.
                    </FieldHint>
                  </Label>
                </div>
                <div>
                  <Label className="text-xs text-stone-500 uppercase">Attivo nei listini</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {pricelistTypes.map(pt => {
                      const checked = plVisible(editProduct, pt);
                      return (
                        <label key={pt.id} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs cursor-pointer transition-colors ${checked ? "bg-[#8A1C2A] text-white border-[#8A1C2A]" : "bg-white border-stone-300 text-stone-700 hover:border-[#8A1C2A]"}`} data-testid={`restrict-pl-${pt.id}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => setEditProduct({ ...editProduct, pricelist_active: { ...(editProduct.pricelist_active || {}), [pt.id]: e.target.checked } })}
                            className="sr-only"
                          />
                          {pt.name} ({(pt.commission * 100).toFixed(1)}%)
                        </label>
                      );
                    })}
                  </div>
                  {editProduct.pricelist_default_active === false && <p className="text-[11px] text-stone-500 mt-1">Articolo creato per un listino specifico: nei nuovi listini resta spento finché non lo attivi.</p>}
                </div>
                <div>
                  <Label className="text-xs text-stone-500 uppercase">Agenti autorizzati (specifici)</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {(agentsList || []).map(ag => {
                      const arr = editProduct.restricted_agents || [];
                      const checked = arr.includes(ag.id);
                      return (
                        <label key={ag.id} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs cursor-pointer transition-colors ${checked ? "bg-[#C5A059] text-white border-[#C5A059]" : "bg-white border-stone-300 text-stone-700 hover:border-[#C5A059]"}`} data-testid={`restrict-ag-${ag.id}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...arr, ag.id]
                                : arr.filter(x => x !== ag.id);
                              setEditProduct({ ...editProduct, restricted_agents: next });
                            }}
                            className="sr-only"
                          />
                          {ag.name} ({((ag.commission_rate || 0) * 100).toFixed(1)}%)
                        </label>
                      );
                    })}
                    {(!agentsList || agentsList.length === 0) && <span className="text-xs text-stone-400 italic">{t.noAgents}</span>}
                  </div>
                </div>
              </div>

              {/* Provvigione + Sconto personalizzati per listino + Calcolo bidirezionale */}
              <div className="space-y-3 pt-2 border-t border-stone-200">
                <div>
                  <Label className="text-stone-700 flex items-center gap-2 font-semibold">
                    <Calculator className="w-4 h-4 text-[#8A1C2A]" />
                    Provvigione, Sconto & Prezzo per listino
                    <FieldHint>
                      Permette di sovrascrivere i parametri di calcolo del prodotto <em>specifici per ogni listino</em>.<br/><br/>
                      <strong>Provv.</strong> → override commissione per questo articolo su questo listino<br/>
                      <strong>Sconto</strong> → sconto in % applicato solo qui<br/>
                      <strong>Prezzo Finale</strong> → si modifica in 2 modi:<br/>
                      🔄 <em>Globale</em>: ricalcola il margine del prodotto → impatta tutti i listini<br/>
                      🔒 <em>Fisso</em>: fissa il prezzo SOLO su questo listino senza alterare gli altri (no sconto visibile in PDF)<br/><br/>
                      Lascia vuoto per usare i default del listino.
                    </FieldHint>
                  </Label>
                  <p className="text-xs text-stone-500 mt-1">
                    Lascia vuoto per usare i default. Modifica il <strong>Prezzo Finale</strong>:
                    <br />
                    🔄 <strong>Modalità globale</strong>: ricalcola il margine → impatta tutti i listini
                    <br />
                    🔒 <strong>Modalità fissa</strong> (click sul bottone): fissa il prezzo solo per QUESTO listino, senza toccare gli altri. Nessuno sconto visibile nel PDF.
                  </p>
                </div>
                <div className="space-y-2">
                  {pricelistTypes.map(pt => {
                    const commOverrides = editProduct.commission_overrides || {};
                    const discOverrides = editProduct.discount_overrides || {};
                    const priceOverridesMap = editProduct.price_overrides || {};
                    const commOverrideVal = commOverrides[pt.id];
                    const discOverrideVal = discOverrides[pt.id];
                    const hasFixedPrice = priceOverridesMap[pt.id] !== undefined && priceOverridesMap[pt.id] !== null;
                    const isCommOverridden = commOverrideVal !== undefined && commOverrideVal !== null && commOverrideVal !== "";
                    const isDiscOverridden = discOverrideVal !== undefined && discOverrideVal !== null && discOverrideVal !== "";
                    const effComm = isCommOverridden ? parseFloat(commOverrideVal) : (pt.commission || 0);
                    const effDisc = isDiscOverridden ? parseFloat(discOverrideVal) : (pt.discount || 0);
                    const base = calculateBasePrice(editProduct.acq || 0, editProduct.bank || 0, editProduct.marg || 0, editProduct.extra || 0);
                    const formulaPrice = calculateFinalPrice(base, effComm, pt.markup || 0, effDisc);
                    const finalPrice = hasFixedPrice ? priceOverridesMap[pt.id] : formulaPrice;
                    const isOverridden = isCommOverridden || isDiscOverridden || hasFixedPrice;
                    return (
                      <div key={pt.id} className={`p-3 rounded border ${hasFixedPrice ? 'bg-stone-100 border-stone-400' : (isOverridden ? 'bg-[#C5A059]/10 border-[#C5A059]' : 'bg-white border-stone-200')}`}>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <p className="text-sm font-medium text-stone-800 truncate">{pt.name}</p>
                          {hasFixedPrice ? (
                            <span className="text-[10px] text-stone-700 uppercase font-bold">🔒 Prezzo fissato</span>
                          ) : (
                            isOverridden && <span className="text-[10px] text-[#C5A059] uppercase font-bold">Override</span>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2 items-end">
                          <div>
                            <Label className="text-[10px] text-stone-500 uppercase">Provv. (default {(pt.commission * 100).toFixed(1)}%)</Label>
                            <PercentInput
                              value={isCommOverridden ? (commOverrideVal || 0) : null}
                              placeholder={`Default ${((pt.commission || 0) * 100).toFixed(1)}%`}
                              disabled={hasFixedPrice}
                              onChange={(v) => {
                                const next = { ...(editProduct.commission_overrides || {}) };
                                if (v === null || v === undefined || v === 0) delete next[pt.id]; else next[pt.id] = v;
                                setEditProduct({ ...editProduct, commission_overrides: next });
                              }}
                              data-testid={`commission-override-${pt.id}`}
                            />
                            <p className="text-[10px] text-stone-500 mt-0.5">= {(effComm * 100).toFixed(1)}%</p>
                          </div>
                          <div>
                            <Label className="text-[10px] text-stone-500 uppercase">🏷️ Sconto (def {(pt.discount * 100).toFixed(1)}%)</Label>
                            <PercentInput
                              value={isDiscOverridden ? (discOverrideVal || 0) : null}
                              placeholder={`Default ${((pt.discount || 0) * 100).toFixed(1)}%`}
                              disabled={hasFixedPrice}
                              onChange={(v) => {
                                const next = { ...(editProduct.discount_overrides || {}) };
                                if (v === null || v === undefined || v === 0) delete next[pt.id]; else next[pt.id] = v;
                                setEditProduct({ ...editProduct, discount_overrides: next });
                              }}
                              data-testid={`discount-override-${pt.id}`}
                            />
                            <p className="text-[10px] text-stone-500 mt-0.5">= {(effDisc * 100).toFixed(1)}%</p>
                          </div>
                          <div>
                            <Label className="text-[10px] text-stone-500 uppercase">Prezzo Finale</Label>
                            <ReverseFinalPriceCell pt={pt} effective={effComm} discount={effDisc} computedPrice={finalPrice} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {pricelistTypes.length === 0 && (
                    <p className="text-xs text-stone-400 italic">{t.noPricelistType}</p>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="border-stone-300">{t.cancel}</Button>
            <Button onClick={saveProduct} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="product-save-btn">{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="bg-white border-stone-200">
          <DialogHeader><DialogTitle className="text-stone-900">{t.importExcel}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-600">{t.uploadFile}</p>
            <Input type="file" accept=".xlsx,.xls" onChange={(e) => setUploadFile(e.target.files?.[0])} className="bg-white border-stone-300 file:bg-[#8A1C2A] file:text-white file:border-0 file:px-3 file:py-1 file:rounded" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsImportDialogOpen(false)} className="border-stone-300">{t.cancel}</Button>
            <Button onClick={handleImport} disabled={!uploadFile || uploading} className="bg-[#8A1C2A] text-white">{uploading ? "..." : t.import}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tech sheet dialog (immagine + scheda tecnica IT/EN + PDF) */}
      <ProductTechSheetDialog
        open={!!techSheetProduct}
        product={techSheetProduct}
        onClose={() => setTechSheetProduct(null)}
        onSaved={fetchProducts}
      />
      <QuickPricelistDialog open={quickOpen} pricelist={null} onClose={() => setQuickOpen(false)} onSaved={() => { fetchPricelistTypes(); fetchProducts(); }} />
    </div>
  );
};


export default ProductsManagement;

